<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { platform } from "../../utils/platform";
import { barGap, barItems, barSizeState } from "../../states/roomBarState";
import { fullScreenModeState } from "../../states/fullScreenMode";
import { activeRoomState } from "../../states/active_room";
import { existSharingNotThisUser } from "../../states/screenShareState";

const container = ref<HTMLElement | null>(null);
let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  resizeObserver = new ResizeObserver((entries) => {
    entries.forEach((entry) => {
      const el = entry.target as HTMLElement;
      const isRoomBar = el.dataset.type === "room-bar";
      if (!el.clientWidth) {
        return;
      }
      if (isRoomBar) {
        barSizeState.currentBarWidth = el.clientWidth;
      } else {
        const name = el.dataset.name as string;
        barSizeState.widths[name] = el.clientWidth;
      }
    });
  });
  resizeObserver.observe(container.value!);
  if (cached.length > 0) {
    cached.forEach((el) => {
      resizeObserver!.observe(el);
    });
    cached.length = 0;
  }
});

const cached = [] as HTMLElement[];
function setItem(el: HTMLElement | null) {
  if (el) {
    if (resizeObserver) {
      resizeObserver.observe(el);
    } else {
      cached.push(el);
    }
  }
}
const centerTransform = computed(() => {
  const isSpacer = !!barItems.value.center.find((el) => el.name === "reaction-spacer")
  if(isSpacer) {
    //NOTE: 44 - ширина кнопки реакции в неактивном состоянии
    const offset = (44 + barGap.value) / 2
    return `translateX(${offset}px)`
  } else {
    return "translateX(0px)"
  }
})
</script>
<template>
  <div
    ref="container"
    class="room-bar"
    data-type="room-bar"
    :class="{
      [platform]: true,
      floating:
        fullScreenModeState &&
        activeRoomState.mode === 'stream' &&
        existSharingNotThisUser()
    }"
    :style="{
      padding: `${barSizeState.yPadding}px ${barSizeState.xPadding}px`,
      gap: `${barGap}px`,
    }"
  >
    <div
      class="left"
      :style="{
        gap: `${barGap}px`,
      }"
    >
      <template v-for="item in barItems.left">
        <div
          :ref="(item) => setItem(item as HTMLElement)"
          class="item-wrapper"
          :data-name="item.name"
        >
          <component :is="item.bar!.render" />
        </div>
      </template>
    </div>
    <div
      class="center"
      :style="{
        gap: `${barGap}px`,
        transform: centerTransform
      }"
    >
      <template v-for="item in barItems.center">
        <div
          :ref="(item) => setItem(item as HTMLElement)"
          class="item-wrapper"
          :data-name="item.name"
        >
          <component :is="item.bar!.render" />
        </div>
      </template>
    </div>
    <div
      class="right"
      :style="{
        gap: `${barGap}px`,
      }"
    >
      <template v-for="item in barItems.right">
        <div
          :ref="(item) => setItem(item as HTMLElement)"
          class="item-wrapper"
          :data-name="item.name"
        >
          <component :is="item.bar!.render" />
        </div>
      </template>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.room-bar {
  width: 100%;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  transition: gap 0.3s ease-in-out;
  background-color: transparent;
}

.room-bar.floating {
  background-color: #1e1818;
  border-radius: 16px;
}

.room-bar .item-wrapper {
  width: max-content;
  height: max-content;
}

.left,
.center,
.right {
  display: flex;
  gap: 24px;
  align-items: center;
  height: 100%;
  transition: gap 0.3s ease-in-out;
}

.mobile {
  .left,
  .center,
  .right {
    gap: 12px;
  }
}

.left {
  justify-content: start;
}

.center {
  justify-content: center;
}

.right {
  justify-content: end;
}
</style>
